/**
 * @file wms-error-text.ts
 * @description Человеческие тексты складских ошибок API (phase19.14, W13/W14;
 * коды документа «Перемещение» и нехватки остатка при сборке — phase19.23
 * этап B).
 *
 * Реестр `code → текст` вместо if-каскадов по коду в каждом экране: новая
 * ошибка сервера = запись здесь, экраны не меняются
 * (docs/processes/engineering-principles.md, OCP).
 *
 * Правило: если сервер прислал `details`, собираем текст сами (он точнее и
 * единообразнее по всей админке); нет деталей — показываем `message` сервера,
 * он уже написан по-русски. Общий фолбэк — только на не-ApiError.
 *
 * Подсказку сервера (`message`) не выбрасываем там, где она несёт смысл: для
 * `picking_reservations_unassigned` сервер объясняет причину расхождения, а
 * детали лишь перечисляют строки — печатаем и то, и другое.
 */

import { ApiError } from "./api-client";

/** Строка резерва вне задания (зеркало `picking-execution.service.ts`). */
interface WmsUnassignedItem {
  skuCode?: string | null;
  /** Код склада: сервер отдаёт именно код, не название. */
  warehouseCode?: string | null;
  qty?: string | number | null;
}

/** Строка нехватки остатка под сборку (`picking_stock_missing`). */
interface WmsMissingStockItem {
  skuCode?: string | null;
  required?: string | number | null;
  available?: string | number | null;
}

/** Детали складских ошибок — объединение полей всех известных кодов. */
interface WmsErrorDetails {
  /** `pick_lot_insufficient`: номер партии и её остаток. */
  batchNumber?: string | null;
  available?: string | number | null;
  required?: string | number | null;
  /** `picking_reservations_unassigned`: чего не хватает в задании. */
  skuCodes?: string[] | null;
  items?: (WmsUnassignedItem & WmsMissingStockItem)[] | null;
  /** `warehouse_has_active_reservations`: сколько резервов держит склад. */
  activeReservations?: number | null;
  /** Перемещение (phase19.23 этап B): что стоит в строке и что есть на складе. */
  quantity?: string | number | null;
  physical?: string | number | null;
  reserved?: string | number | null;
  /** `invalid_transfer_source`: виды документа, которых сервер не знает. */
  source?: string[] | null;
  /** `invalid_period`: границы, на которых сервер споткнулся. */
  from?: string | null;
  to?: string | null;
  /** `bin_scan_required` (phase19.22): адрес строки, который надо отсканировать. */
  binCode?: string | null;
  /** `receipt_item_price_invalid` (phase19.31): какое поле цены не принято. */
  field?: string | null;
}

/** Подписи полей цены строки приёмки — как в форме (`ReceiptItemLotFields`). */
const RECEIPT_PRICE_FIELD_LABEL: Record<string, string> = {
  unitCost: "Закупочная ₽",
  salePrice: "Цена продажи ₽",
};

/** Сколько строк деталей показываем в тосте, прежде чем свернуть в «ещё N». */
const MAX_DETAIL_LINES = 3;

/** Число из Decimal-строки сервера в читаемый вид («2.000» → «2»). */
function num(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? String(parsed) : String(value);
}

/**
 * Список строк с обрезкой: три позиции читаются в тосте, дальше — счётчик.
 * Полный разбор менеджер всё равно делает в карточке заказа.
 */
function capList(lines: string[]): string {
  if (lines.length <= MAX_DETAIL_LINES) return lines.join("; ");
  const rest = lines.length - MAX_DETAIL_LINES;
  return `${lines.slice(0, MAX_DETAIL_LINES).join("; ")}; ещё ${rest}`;
}

/** «АРТ-1 · SKLAD-1 · 2» — пропуская поля, которых сервер не прислал. */
function formatUnassignedItem(item: WmsUnassignedItem): string {
  const qty = num(item.qty);
  return [item.skuCode, item.warehouseCode, qty]
    .filter((part): part is string => Boolean(part))
    .join(" · ");
}

/** «АРТ-1 — нужно 2, свободно 1»: `available` сервер шлёт не всегда. */
function formatMissingStockItem(item: WmsMissingStockItem): string {
  if (!item.skuCode) return "";
  const required = num(item.required);
  const available = num(item.available);
  if (required === null) return item.skuCode;
  const tail = available === null ? "" : `, свободно ${available}`;
  return `${item.skuCode} — нужно ${required}${tail}`;
}

/**
 * Реестр складских кодов. Билдер возвращает null, если деталей не хватило, —
 * тогда показываем текст сервера.
 */
const WMS_ERROR_TEXT: Record<
  string,
  (details: WmsErrorDetails, message: string) => string | null
> = {
  // Партию списали или переместили, пока кладовщик отмечал позицию.
  pick_lot_insufficient: (d) => {
    const available = num(d.available);
    const required = num(d.required);
    if (available === null || required === null) return null;
    const lot = d.batchNumber ? `В партии ${d.batchNumber}` : "В партии";
    return (
      `${lot} доступно ${available}, нужно ${required}. ` +
      "Выберите другую партию или отметьте недостачу."
    );
  },
  // Состав заказа правили после постановки задания: резерв есть, строки нет.
  picking_reservations_unassigned: (d, message) => {
    const rows = (d.items ?? [])
      .map(formatUnassignedItem)
      .filter((line) => line.length > 0);
    const list =
      rows.length > 0
        ? capList(rows)
        : capList((d.skuCodes ?? []).filter((code): code is string => Boolean(code)));
    if (!list) return null;
    // Подсказка сервера объясняет причину; своя — на случай пустого message.
    const hint =
      message.trim() || "Обратитесь к менеджеру — задание нужно пересобрать.";
    return `Резервы заказа вне задания: ${list}. ${hint}`;
  },
  // Проведение сборки: под строки без резерва не хватило свободного остатка.
  picking_stock_missing: (d) => {
    const rows = (d.items ?? [])
      .map(formatMissingStockItem)
      .filter((line) => line.length > 0);
    if (rows.length === 0) return null;
    return (
      `На складе не хватает товара: ${capList(rows)}. ` +
      "Оформите недостачу по этим позициям или пополните склад и проведите " +
      "сборку заново."
    );
  },
  // Позиция заказа без фасовки (OQ-50): собрать её нельзя, чинится правкой
  // ЗАКАЗА — пересборка задания тут не поможет, и это надо сказать прямо.
  picking_item_unlinked: () =>
    "Позиция не привязана к товару каталога — проведение недоступно, исправьте заказ.",
  // Правка строк задания, по которому недостача уже оформлена документом:
  // тихое снятие отметки оставило бы задание в SHORTAGE без единой недостачи.
  picking_shortage_declared: () =>
    "Недостача оформлена по заданию — верните задание в очередь, чтобы править строки.",
  // Возврат в очередь по закрытому заказу: собирать уже нечего.
  picking_order_closed: () =>
    "Заказ закрыт или отменён — задание нельзя вернуть в очередь.",
  // Печать акта по черновику: документ ещё ничего не списал.
  writeoff_not_committed: () =>
    "Акт печатается только по проведённому списанию — сначала проведите документ.",
  // ─── Документ «Перемещение» (phase19.23 этап B) ─────────────────────────────
  // Строка увозит больше, чем свободно: часть остатка занята заказами.
  quantity_exceeds_available: (d) => {
    const inRow = num(d.quantity);
    const available = num(d.available);
    if (inRow === null || available === null) return null;
    const reserved = num(d.reserved);
    const tail = reserved && Number(reserved) > 0 ? ` Резерв под заказы ${reserved} шт.` : "";
    return (
      `Доступно ${available} шт, в строке ${inRow}.${tail} ` +
      "Смена склада отгрузки по заказу делается в самом заказе."
    );
  },
  // Внутри склада партия переезжает только целиком (W14).
  partial_move_not_allowed: (d) => {
    const inRow = num(d.quantity);
    const available = num(d.available);
    if (inRow === null || available === null) return null;
    return (
      `Внутри склада партия переезжает целиком: в ячейке ${available} шт, ` +
      `в строке ${inRow}. Уберите количество или выберите другой склад.`
    );
  },
  // Частичный межскладский переезд с ячейкой: два адреса одним полем не описать.
  target_bin_requires_full_move: () =>
    "Ячейку назначения можно указать, только когда уезжает вся партия. " +
    "Уберите ячейку либо увезите остаток целиком.",
  // Строка никуда не едет.
  transfer_same_location: () =>
    "Партия уже лежит в этом месте — выберите другую ячейку или склад.",
  // Перестановка внутри склада без партии: адрес хранения живёт на партии.
  transfer_lot_required: () =>
    "Перестановка внутри склада возможна только для партии — выберите партию в строке.",
  // Проведение пустого документа.
  transfer_empty: () => "Добавьте хотя бы одну строку — пустое перемещение не проводится.",
  // Второй клик по «Провести», пока первый в полёте.
  transfer_already_committing: () =>
    "Перемещение уже проводится. Дождитесь результата — повтор задвоил бы движения.",
  // Ячейка на складе, где полок не бывает (транзит, ПВЗ).
  bins_not_supported: () => "У этого склада ячеек не бывает — уберите адрес в строке.",
  // Просрочка на склад, с которого идут продажи.
  lot_expired: () =>
    "Партия просрочена — на склад, с которого идут продажи, её перемещать нельзя. " +
    "Спишите партию или отправьте её в карантин.",
  // Одна партия — одна строка документа.
  transfer_lot_already_in_document: () =>
    "Эта партия уже есть в документе — измените существующую строку.",
  // Печать накладной по документу без межскладских строк.
  transfer_internal_only: () =>
    "В документе только перестановки внутри склада — накладная не нужна: " +
    "остаток и ответственное лицо не менялись.",
  // Печать накладной по непроведённому документу.
  transfer_not_committed: () =>
    "Накладная печатается только по проведённому перемещению — сначала проведите документ.",
  // Фильтр реестра прислал вид документа, которого сервер не знает: рассинхрон
  // версий админки и API (или ручная правка адресной строки).
  invalid_transfer_source: () =>
    "Фильтр по виду документа устарел — обновите страницу и повторите отбор.",
  // Границы периода не разобрались либо начало позже конца.
  invalid_period: (d) =>
    d.from && d.to
      ? "Начало периода позже его конца — выберите период заново."
      : "Период фильтра задан неверно — выберите его заново.",
  // ─── Этикетки и срок годности (phase19.22) ──────────────────────────────────
  // Gotenberg/хранилище недоступны: печать не случилась, приёмка цела.
  label_render_failed: () =>
    "Сервис печати сейчас недоступен. Приёмка проведена и не пострадала — повторите через минуту.",
  code_mode_not_supported: () =>
    "На мини-этикетке помещается только DataMatrix — выберите «Оба» или «DataMatrix».",
  receipt_not_committed: () =>
    "Этикетки печатаются после проведения приёмки — сначала проведите документ.",
  duplicate_lot: () => "Одна партия выбрана дважды — снимите лишнюю строку.",
  labels_limit_exceeded: () =>
    "Слишком много этикеток за раз (не больше 5000) — разбейте печать на части.",
  label_items_empty: () => "Отметьте позиции и укажите количество.",
  invalid_start_at: () => "«Начать с наклейки» — номер клетки на листе, от 1 до числа наклеек.",
  label_printer_invalid: () => "Принтер не подходит: выберите принтер этикеток этого склада.",
  label_sheet_overflow: () =>
    "Наклейки не помещаются на лист A4 — уменьшите поля или зазоры.",
  label_internal_code_invalid: () =>
    "У фасовки битый код склада — печать остановлена. Сообщите администратору.",
  lot_expiry_mismatch: () =>
    "У партии с этим номером уже другой срок годности: одна партия — один срок. " +
    "Укажите другой номер партии или оставьте поле пустым.",
  receipt_not_draft: () =>
    "Приёмка уже проведена — срок вписывается через «⋯ → Указать срок» у строки.",
  receipt_item_quantity_invalid: () => "Количество в строке должно быть больше нуля.",
  // Правка цены строки черновика (phase19.31, контракт §15): текст сервера + поле.
  receipt_item_price_invalid: (d, message) => {
    const text = message.trim() || "Цена должна быть неотрицательным числом.";
    const label = d.field ? RECEIPT_PRICE_FIELD_LABEL[d.field] : undefined;
    return label ? `Поле «${label}»: ${text}` : text;
  },
  expiry_already_set: () => "Срок у партии уже указан — менять его нельзя (W20).",
  expiry_invalid: () => "Срок годности указан неверно — выберите дату заново.",
  // Внутренний выпуск без проведённого списания сырья не проводится (2026-09-26).
  parent_writeoff_required: () =>
    "Внутренний выпуск проводится только с привязанным списанием сырья «В производство» — укажите парное списание.",
  parent_writeoff_not_committed: () =>
    "Парное списание «В производство» ещё черновик — проведите списание сырья, затем приёмку.",
  receipt_parent_writeoff_not_production: () =>
    "Парным может быть только списание с причиной «В производство».",
  // Сборка: отметка требует подтверждения ячейки сканом (W14-9).
  bin_scan_required: (d) => {
    const binCode = d.binCode;
    return binCode ? `Отсканируйте ячейку ${binCode}` : "Отсканируйте ячейку позиции";
  },
  lot_mismatch_reason_required: () => "Укажите причину замены партии.",
  // Снятие флага «доступен для продажи» у склада с занятым товаром.
  warehouse_has_active_reservations: (d) => {
    const count = d.activeReservations;
    if (typeof count !== "number") return null;
    return (
      `Склад держит ${count} активных резервов. ` +
      "Сначала соберите или снимите эти заказы, потом убирайте из продажи."
    );
  },
};

/**
 * Текст ошибки для toast/inline-Alert.
 *
 * @param err   пойманное исключение (ApiError или что угодно);
 * @param fallback текст для сетевых и неизвестных сбоев.
 */
export function wmsErrorText(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback;
  const build = WMS_ERROR_TEXT[err.code];
  if (build) {
    const text = build((err.details ?? {}) as WmsErrorDetails, err.message ?? "");
    if (text) return text;
  }
  return err.message || fallback;
}
